import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    apiGetDrugs,
    apiGetDrugDetail,
    apiEditDrug,
    apiGetActiveIngredients,
    apiGetDosageUnits,
} from '../api/drugs/drugs';
import { handleApiError } from '../lib/errorHandler';
import { TMed, TMedsList, TKiroDetail } from '../context/MedDirProvider';

export function useMedicationOperations() {
    const { t } = useTranslation();
    const [medsList, setMedsList] = useState<TMedsList>({
        count: 0,
        results: [],
    });
    const [drugDetail, setDrugDetail] = useState<TMed | null>(null);
    const [loadingMed, setLoadingMed] = useState(false);

    const getDrugList = async (
        useInKiro: boolean | undefined,
        search: string | undefined,
        limit: number,
        offset: number,
        setAlertMessages: (msgs: Partial<Record<string, string[]>>) => void
    ) => {
        setLoadingMed(true);
        try {
            const response = await apiGetDrugs({ limit, offset, useInKiro, search });
            if (response.data) {
                setMedsList(response.data);
            }
        } catch (error: any) {
            const serverError = handleApiError(error, t('errors.medication.loadingList'), 'medication');
            setAlertMessages(serverError as  Partial<Record<string, string[]>>);
        } finally {
            setLoadingMed(false);
        }
    };

    const getDrugDetail = async (
        id: string,
        setAlertMessages: (msgs: Partial<Record<string, string[]>>) => void
    ) => {
        try {
            const response = await apiGetDrugDetail(id);
            if (response.data) {
                setDrugDetail(response.data);
            }
        } catch (error: any) {
            const serverError = handleApiError(error, t('errors.medication.loadingDetail'), 'medication');
            setAlertMessages(serverError as  Partial<Record<string, string[]>>);
        }
    };

    const getActiveIngredients = async (
        setAlertMessages: (msgs: Partial<Record<string, string[]>>) => void
    ) => {
        try {
            const response = await apiGetActiveIngredients();
            if (response.data) {
                const { results, count } = response.data;

                if (results.length === count) {
                    return response.data;
                }

                const allIngredients = await apiGetActiveIngredients(count);
                if (allIngredients.data) {
                    return allIngredients.data;
                }
            }
        } catch (error: any) {
            const serverError = handleApiError(error, t('errors.medication.loadingDetail'), 'medication');
            setAlertMessages(serverError as  Partial<Record<string, string[]>>);
        }
    };

    const getDosageUnits = async (
        setAlertMessages: (msgs: Partial<Record<string, string[]>>) => void
    ) => {
        try {
            const response = await apiGetDosageUnits();
            if (response.data) {
                return response.data;
            }
        } catch (error: any) {
            const serverError = handleApiError(error, t('errors.medication.loadingDetail'), 'medication');
            setAlertMessages(serverError as  Partial<Record<string, string[]>>);
        }
    };

    const editDrug = async (
        id: string,
        params: TKiroDetail,
        setAlertMessages: (msgs: Partial<Record<string, string[]>>) => void
    ): Promise<TMed | null> => {
        try {
            const response = await apiEditDrug(id, params);
            if (response.data) {
                setAlertMessages({});
                return response.data;
            }
            return null;
        } catch (error: any) {
            const serverError = handleApiError(error, t('errors.medication.saving'), 'medication');
            setAlertMessages(serverError as  Partial<Record<string, string[]>>);
            return null;
        }
    };

    return {
        medsList,
        drugDetail,
        loadingMed,
        setDrugDetail,
        getDrugList,
        getDrugDetail,
        getActiveIngredients,
        getDosageUnits,
        editDrug,
    };
}
